import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import {
  AppBar,
  Box,
  Drawer,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as ActivityIcon,
  Phone as PhoneIcon,
  LocalOffer as PromoIcon,
  ToggleOn as FlagsIcon,
  HourglassEmpty as WaitlistIcon,
  MonitorHeart as HealthIcon,
  Policy as FTCIcon,
  Security as SendersIcon,
  AccountBalance as FinancialsIcon,
  School as TrainingIcon,
  VpnKey as SecurityIcon,
  Feedback as FeedbackIcon,
  SupportAgent as SupportLineIcon,
  Voicemail as VoicemailIcon,
  Phone as PhoneLogIcon,
  NewReleases as ReleaseNotesIcon,
  QrCode2 as CampaignLinksIcon,
} from '@mui/icons-material';

const drawerWidth = 240;
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [newFeedbackCount, setNewFeedbackCount] = useState(0);
  const [unreadVoicemailCount, setUnreadVoicemailCount] = useState(0);
  const [version, setVersion] = useState('');

  const fetchFeedbackCount = useCallback(async () => {
    try {
      const res = await adminAPI.getNewFeedbackCount();
      setNewFeedbackCount(res.data.count);
    } catch (err) {
      // Silently ignore — non-critical
    }
  }, []);

  const fetchVoicemailCount = useCallback(async () => {
    try {
      const res = await adminAPI.getCallLogUnreadCount();
      setUnreadVoicemailCount(res.data.count);
    } catch (err) {
      // Fall back to voicemail count if call-logs table doesn't exist yet
      try {
        const fallback = await adminAPI.getVoicemailUnreadCount();
        setUnreadVoicemailCount(fallback.data.count);
      } catch (e) {
        // Silently ignore — non-critical
      }
    }
  }, []);

  useEffect(() => {
    fetchFeedbackCount();
    fetchVoicemailCount();
    const interval = setInterval(() => {
      fetchFeedbackCount();
      fetchVoicemailCount();
    }, 15000); // poll every 15s
    const onFeedbackUpdate = () => fetchFeedbackCount();
    const onVoicemailUpdate = () => fetchVoicemailCount();
    window.addEventListener('feedback-updated', onFeedbackUpdate);
    window.addEventListener('voicemail-updated', onVoicemailUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('feedback-updated', onFeedbackUpdate);
      window.removeEventListener('voicemail-updated', onVoicemailUpdate);
    };
  }, [fetchFeedbackCount, fetchVoicemailCount]);

  // Fetch API version on mount
  useEffect(() => {
    fetch(`${API_ORIGIN}/api/version`)
      .then(res => res.json())
      .then(data => setVersion(data.version))
      .catch(() => setVersion(''));
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
    { label: 'Users', path: '/users', icon: <PeopleIcon /> },
    { label: 'Waitlist', path: '/waitlist', icon: <WaitlistIcon /> },
    { label: 'Activity', path: '/activity', icon: <ActivityIcon /> },
    { label: 'Audit Log', path: '/audit-log', icon: <HistoryIcon /> },
    { label: 'Senders', path: '/senders', icon: <SendersIcon /> },
    { label: 'Phone Reputation', path: '/phone-reputation', icon: <PhoneIcon /> },
    { label: 'FTC Data', path: '/ftc-data', icon: <FTCIcon /> },
    { label: 'Financials', path: '/financials', icon: <FinancialsIcon /> },
    { label: 'Training', path: '/training', icon: <TrainingIcon /> },
    { label: 'Feedback', path: '/feedback', icon: <FeedbackIcon />, badge: newFeedbackCount },
    { label: 'Promo Codes', path: '/promo-codes', icon: <PromoIcon /> },
    { label: 'Feature Flags', path: '/feature-flags', icon: <FlagsIcon /> },
    { label: 'Support Line', path: '/support-line', icon: <SupportLineIcon /> },
    { label: 'Call Log', path: '/voicemails', icon: <PhoneLogIcon />, badge: unreadVoicemailCount },
    { label: 'Campaign Links', path: '/campaign-links', icon: <CampaignLinksIcon /> },
    { label: 'Release Notes', path: '/release-notes', icon: <ReleaseNotesIcon /> },
    { label: 'System Health', path: '/system-health', icon: <HealthIcon /> },
    { label: 'Security (2FA)', path: '/security', icon: <SecurityIcon /> },
  ];

  const drawer = (
    <Box>
      <Toolbar sx={{ gap: 1 }}>
        <AdminIcon color="primary" />
        <Typography variant="h6" noWrap component="div" fontWeight={600}>
          Admin
        </Typography>
        <Chip label="Internal" size="small" color="secondary" />
      </Toolbar>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.badge > 0 ? (
                  <Badge badgeContent={item.badge} color="error" max={99}>
                    {item.icon}
                  </Badge>
                ) : item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
      {version && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', py: 1, color: 'text.disabled' }}>
          v{version}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Second Clear Admin
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unreadVoicemailCount > 0 && (
              <IconButton
                color="inherit"
                onClick={() => navigate('/voicemails')}
                title={`${unreadVoicemailCount} missed call${unreadVoicemailCount === 1 ? '' : 's'}`}
              >
                <Badge badgeContent={unreadVoicemailCount} color="error" max={99}>
                  <VoicemailIcon />
                </Badge>
              </IconButton>
            )}
            <Typography variant="body2" color="text.secondary">
              {user?.name || user?.email}
            </Typography>
            <Badge
              color="error"
              variant="dot"
              invisible={newFeedbackCount === 0 && unreadVoicemailCount === 0}
              overlap="circular"
            >
              <Avatar
                sx={{ bgcolor: 'primary.main', width: 32, height: 32, cursor: 'pointer' }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                {(user?.name || user?.email || 'A')[0].toUpperCase()}
              </Avatar>
            </Badge>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            {unreadVoicemailCount > 0 && (
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/voicemails'); }}>
                <ListItemIcon>
                  <Badge badgeContent={unreadVoicemailCount} color="error" max={99}>
                    <PhoneLogIcon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                Missed Calls
              </MenuItem>
            )}
            {newFeedbackCount > 0 && (
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/feedback'); }}>
                <ListItemIcon>
                  <Badge badgeContent={newFeedbackCount} color="error" max={99}>
                    <FeedbackIcon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                New Feedback
              </MenuItem>
            )}
            {(newFeedbackCount > 0 || unreadVoicemailCount > 0) && <Divider />}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          minHeight: '100vh',
          bgcolor: 'background.default',
          overflowX: 'hidden',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;